/* eslint-disable react-hooks/exhaustive-deps,import/no-unresolved */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import {
  AccountsContent,
  WalletsFilter,
  WalletsList,
  Balance,
  ActionButton,
  Body,
  LeftPanel,
} from 'assets/components';
import AddAccountModal from './modals/AddAccountModal';
import WalletDepositModal from './modals/WalletsWithdrawDepositModal';
import SendCryptoModal from './modals/SendCryptoModal';
import { WALLETS_ALL } from './queries';
import {
  RATES_QUERY_KEY,
  SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY,
  USER_PREFERENCES,
  USER_REGION_SETTINGS_QUERY_KEY,
} from '../../utilities/sharedQueries';
import { getTotalBalance } from './partials/helpers';
import { isClCard } from '../../utilities/helpers';
import AccountLayout from '../shared/AccountLayout';
import {
  useUserAllowStakingFeature,
  useUserCardProvider,
  useUserPersonalInfo,
} from '../../utilities/sharedHooks/user';
import {
  useLedgerFilteredAllowedCreationCurrencies,
  useLedgerFilteredCurrencies,
  useLedgerFilteredWallets,
  useSyncLedgerBaanxWallets,
} from '../../utilities/sharedHooks/ledgerWallets';
import { cardProviders, WalletsTypes } from '../Card/constants';
import ExchangeModal from './modals/exchangeModal';
import { getCachedHiddenUserCurrencyList } from '../../utilities/caching';
import useIsUsUser from '../../utilities/sharedHooks/usUser';
import useIsConnectFinancialCustodyUser from '../../utilities/sharedHooks/custodyUser';
import { currencies, currenciesDefault } from '../../utilities/constants';
import { useSelectedWalletType } from './hooks';
import useRiskTierSettings from '../../utilities/sharedHooks/useRiskTierSettings';
import { useAccountRestriction } from '../../utilities/sharedHooks/account';
import { GET_ALL_STAKINGS } from '../Staking/queries';
import { stakingTypes } from '../Staking/constants';
import { useLedgerAccounts } from '../../utilities/sharedHooks/ledgerSDKHooks';
import ModalLayout from '../shared/ModalLayout';
import {
  actionTypes,
  MODAL_SCREENS,
  SendCryptoScreensFlow,
  walletCurrenciesTypes,
} from './constants';
import { TOPUP_ACCOUNTS_QUERY_PARAM, TOPUP_SELECT_CURRENCY } from './topupDeeplink/constants';
import { isManageAssetsRequested } from './manageAssetsDeeplink/constants';
import InfoPopup from '../shared/Modals/InfoPopup';
import { SettingButtonPlaceholder } from '../../components/Placeholders';
import { showAlert } from '../../utilities/notifications';
import { WITHDRAWAL_DEEPLINK_QUERY_PARAMS } from './deeplink/constants';

const Accounts = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentModalScreen, setCurrentModalScreen] = useState('');
  const [showAddAccountModal, setShowAddWallet] = useState(false);
  const [exitedWallets, setExistedWallets] = useState([]);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [showSendCryptoModal, setShowSendCryptoModal] = useState(false);
  const [totalChangeInFiatValue, setTotalChangeInFiatValue] = useState('0.00');
  const [totalBalanceInFiatValue, setTotalBalanceInFiatValue] = useState('0.00');
  const [selectedWalletType, setSelectedWalletType] = useSelectedWalletType();
  const [searchInput, onChangeSearchInput] = useState('');
  const [selectedWallet, setSelectedWallet] = useState(null);
  const location = useLocation();
  const history = useHistory();
  const { data: userPref, refetch: refetchUserPerf } = useQuery(USER_PREFERENCES, {
    enabled: false,
  });
  const [isDeposit, setIsDeposit] = useState(false);
  const [wallets, setWallets] = useState([]);
  const [isAllWalletsLoading, setIsAllWalletsLoading] = useState(true);
  const { cardProvider } = useUserCardProvider();
  const { riskAssessment } = useRiskTierSettings();
  const { t: translate } = useTranslation();
  const isUsUser = useIsUsUser();
  const isConnectFinancialCustodyUser = useIsConnectFinancialCustodyUser();
  const canManageAccounts = !isUsUser && !isConnectFinancialCustodyUser;
  const { data: userRegionSettings, isLoading: isLoadingUserRegionSettings } = useQuery(
    USER_REGION_SETTINGS_QUERY_KEY
  );

  const [ledgerAccounts] = useLedgerAccounts();

  const { isLoading: isRatesLoading, data: rates } = useQuery(RATES_QUERY_KEY);

  const { isUS } = useUserPersonalInfo();
  const isDepositHidden = process.env.REACT_APP_DEPOSIT_BUTTON_HIDDEN && !isUS;

  const allowStakingFeature = useUserAllowStakingFeature();
  const { restrictions, isLoading: isAccountRestrictionLoading } = useAccountRestriction();

  const { data: allWallets, refetch: refetchAllWallets } = useLedgerFilteredWallets(WALLETS_ALL, {
    enabled: false,
    onSettled(data) {
      setWallets(data);
      setIsAllWalletsLoading(false);
    },
  });

  useSyncLedgerBaanxWallets(ledgerAccounts, wallets, refetchAllWallets);

  const {
    data: allStakings,
    refetch: refetchAllStaking,
    isLoading: isLoadingAllStakings,
  } = useQuery(GET_ALL_STAKINGS, {
    enabled: false,
  });

  // this useEffect is for fixing the loop issue when session is expired
  useEffect(() => {
    refetchAllStaking();
    refetchUserPerf();
    refetchAllWallets();
  }, []);

  const fiatCurrencyCode = userPref?.displayCurrency || 'EUR';

  const { data: supportedCurrencies } = useLedgerFilteredCurrencies([
    SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY,
  ]);

  const [showExchangeModal, setShowExchangeModal] = useState(false);
  const toggleExchangeModal = () => setShowExchangeModal((prev) => !prev);

  const { data: enabledLedgerFilteredCurrencies, isLoading: isLoadingLedgerCurrencies } =
    useLedgerFilteredAllowedCreationCurrencies([
      SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY,
      {
        can_create_wallet: true,
      },
    ]);

  const isWalletsCreationsDisabled = useMemo(() => {
    if (enabledLedgerFilteredCurrencies && riskAssessment) {
      if (riskAssessment.settings) {
        return !(
          riskAssessment.userRisk[`${selectedWalletType}_wallets`] &&
          Object.keys(enabledLedgerFilteredCurrencies).length > 0
        );
      }
      return !Object.keys(enabledLedgerFilteredCurrencies).length > 0;
    }
    return false;
  }, [enabledLedgerFilteredCurrencies, riskAssessment]);

  const cacheHiddenCurrency = useCallback(
    (wallet) => getCachedHiddenUserCurrencyList().indexOf(wallet.coin.toUpperCase()) === -1,
    [wallets]
  );

  const stakingAmount = useMemo(() => {
    if (allStakings && allStakings?.length > 0) {
      return allStakings
        .filter((item) => item.state === stakingTypes.ACTIVE)
        .reduce((acc, item) => acc + parseFloat(item.amount), 0);
    }
    return 0;
  }, [allStakings]);

  useEffect(() => {
    const { totalBalanceInFiat, totalChangeInFiat } = getTotalBalance(
      fiatCurrencyCode,
      (wallets || []).filter(cacheHiddenCurrency),
      rates,
      allowStakingFeature ? stakingAmount : 0
    );
    setTotalChangeInFiatValue(totalChangeInFiat * 100);
    setTotalBalanceInFiatValue(totalBalanceInFiat);
  }, [fiatCurrencyCode, rates, wallets]);

  const ledgerAccountWallet = useCallback(
    (wallet) => {
      if (ledgerAccounts) {
        if (
          supportedCurrencies &&
          supportedCurrencies[wallet?.coin] &&
          supportedCurrencies[wallet?.coin].is_erc20_token &&
          ledgerAccounts[currencies.ETH.icon.toLowerCase()]
        ) {
          return ledgerAccounts[currencies.ETH.icon.toLowerCase()];
        }
        return ledgerAccounts[wallet?.coin];
      }
      return null;
    },
    [ledgerAccounts]
  );

  const onDepositClick = (wallet) => {
    setShowDepositModal(true);
    setSelectedWallet(wallet);
    setIsDeposit(true);
  };
  const onWithdrawalClick = (wallet) => {
    setShowDepositModal(true);
    setSelectedWallet(wallet);
    setIsDeposit(false);
  };
  const onSendClick = (wallet, withBack = false) => {
    if (withBack) {
      setShowDepositModal(false);
    }
    setShowSendCryptoModal(true);
    setSelectedWallet(wallet);
  };

  const onExchangeClick = (wallet) => {
    setSelectedWallet(wallet);
    toggleExchangeModal();
  };

  // opens SendCryptoModal for the currency requested by the withdrawal deeplink (src/screens/Accounts/deeplink)
  useEffect(() => {
    if (isAllWalletsLoading || !supportedCurrencies || !restrictions || isAccountRestrictionLoading)
      return;
    const searchParams = new URLSearchParams(location.search);
    const currency = searchParams.get(WITHDRAWAL_DEEPLINK_QUERY_PARAMS.CURRENCY);
    if (!currency) return;

    const matchedWallet = wallets.find(
      (wallet) => wallet.coin?.toUpperCase() === currency.toUpperCase()
    );

    if (matchedWallet) {
      if (restrictions[matchedWallet?.coin]?.sendDisabled) {
        showAlert({ message: translate('withdrawal_action_not_allowed'), type: 'danger' });
        history.replace(location.pathname);
        return;
      }

      const isStableCoin = supportedCurrencies[matchedWallet.coin]?.fiat_currency_peg;

      const isCryptoWallet =
        matchedWallet.wallet_type?.toLowerCase() === walletCurrenciesTypes.CRYPTO;

      if (isCryptoWallet || isStableCoin) {
        onSendClick(matchedWallet);
      } else {
        onWithdrawalClick(matchedWallet);
      }
    } else {
      showAlert({ message: translate('withdrawal_currency_not_available'), type: 'danger' });
    }
    history.replace(location.pathname);
  }, [
    isAllWalletsLoading,
    wallets,
    location.search,
    supportedCurrencies,
    restrictions,
    isAccountRestrictionLoading,
  ]);

  // opens WalletDepositModal for the currency requested by the topup deeplink
  // (src/screens/Accounts/topupDeeplink); a currency that is unknown or cannot
  // receive falls back to the wallet picker, which already greys out those
  // coins and is where the user can add a wallet
  useEffect(() => {
    if (isAllWalletsLoading || !supportedCurrencies || !restrictions || isAccountRestrictionLoading)
      return;
    const searchParams = new URLSearchParams(location.search);
    const topupCurrency = searchParams.get(TOPUP_ACCOUNTS_QUERY_PARAM);
    if (!topupCurrency) return;

    const matchedWallet =
      topupCurrency === TOPUP_SELECT_CURRENCY
        ? null
        : wallets.find((wallet) => wallet.coin?.toUpperCase() === topupCurrency.toUpperCase());
    const canTopUpWallet =
      matchedWallet && !restrictions[matchedWallet.coin?.toLowerCase()]?.receiveDisabled;

    if (canTopUpWallet) {
      onDepositClick(matchedWallet);
    } else {
      setActionType(actionTypes.ADD_FUNDS);
      setSelectedWallet(null);
      setShowDepositModal(true);
    }
    history.replace(location.pathname);
  }, [
    isAllWalletsLoading,
    wallets,
    location.search,
    supportedCurrencies,
    restrictions,
    isAccountRestrictionLoading,
  ]);

  useEffect(() => {
    if (isAllWalletsLoading || !isManageAssetsRequested(location.search)) return;
    setShowAddWallet(true);
    history.replace(location.pathname);
  }, [isAllWalletsLoading, location.search]);

  const showModal = (screen) => {
    setCurrentModalScreen(screen);
    setIsModalVisible(true);
  };

  const filteredWalletsByType = useMemo(() => {
    if (wallets && !isAllWalletsLoading) {
      return wallets.filter((wallet) => {
        let allowedWallet = false;
        if (selectedWalletType === WalletsTypes.CRYPTO) {
          allowedWallet =
            wallet?.wallet_type?.toLowerCase() === selectedWalletType && !wallet?.is_stablecoin;
        } else if (selectedWalletType === WalletsTypes.FIAT) {
          allowedWallet =
            wallet?.wallet_type?.toLowerCase() === selectedWalletType || wallet?.is_stablecoin;
        }
        return allowedWallet;
      });
    }

    return [];
  }, [selectedWalletType, wallets, isAllWalletsLoading]);

  const filteredWallets = useMemo(() => {
    if (filteredWalletsByType) {
      return filteredWalletsByType.filter(
        (wallet) =>
          cacheHiddenCurrency(wallet) &&
          (wallet.coin?.toLowerCase().indexOf(searchInput.toLowerCase()) !== -1 ||
            (currenciesDefault[wallet.coin.toUpperCase()]?.fullName &&
              currenciesDefault[wallet.coin.toUpperCase()]?.fullName
                ?.toLowerCase()
                ?.indexOf(searchInput.toLowerCase()) !== -1) ||
            (currenciesDefault[wallet.coin.toUpperCase()]?.currencyName &&
              currenciesDefault[wallet.coin.toUpperCase()]?.currencyName
                ?.toLowerCase()
                ?.indexOf(searchInput.toLowerCase()) !== -1)) &&
          !isClCard(wallet.coin)
      );
    }
    return [];
  }, [searchInput, selectedWalletType, filteredWalletsByType]);

  const createdWallets = useMemo(() => {
    if (wallets) {
      return wallets.filter(
        (wallet) =>
          cacheHiddenCurrency(wallet) &&
          (wallet.coin?.toLowerCase().indexOf(searchInput.toLowerCase()) !== -1 ||
            (currenciesDefault[wallet.coin.toUpperCase()]?.fullName &&
              currenciesDefault[wallet.coin.toUpperCase()]?.fullName
                ?.toLowerCase()
                ?.indexOf(searchInput.toLowerCase()) !== -1) ||
            (currenciesDefault[wallet.coin.toUpperCase()]?.currencyName &&
              currenciesDefault[wallet.coin.toUpperCase()]?.currencyName
                ?.toLowerCase()
                ?.indexOf(searchInput.toLowerCase()) !== -1)) &&
          !isClCard(wallet.coin)
      );
    }
    return [];
  }, [searchInput, selectedWalletType, filteredWalletsByType]);

  useEffect(() => {
    setExistedWallets(createdWallets.map((wallet) => wallet.coin.toUpperCase()));
  }, [filteredWallets]);

  const showEmptyAccountScreen = useMemo(() => {
    if (allWallets && filteredWalletsByType) {
      const visibleWalletList = filteredWalletsByType.filter((wallet) =>
        cacheHiddenCurrency(wallet)
      );
      return allWallets.length === 0 || visibleWalletList.length === 0;
    }
    return false;
  }, [allWallets, filteredWalletsByType]);

  const isLoading =
    isRatesLoading ||
    isLoadingAllStakings ||
    isAllWalletsLoading ||
    isLoadingLedgerCurrencies ||
    isLoadingUserRegionSettings;

  const renderDisclaimer = () => (
    <div className="col-md-3 ps-0 text-f7 text-shadow mt-6">
      <div>{translate('us_bank_disclaimer_visa_2')}</div>
    </div>
  );

  const infoPopupOptions = {
    title: '',
    content: () => (
      <p className="text-white text-f6 mt-2">{translate('daily_total_change_info')}</p>
    ),
  };

  return (
    <>
      <AccountLayout
        hasHeader={false}
        browserChildren={
          <>
            <LeftPanel>
              <Balance
                totalChangeInFiat={totalChangeInFiatValue}
                totalBalanceInFiat={totalBalanceInFiatValue}
                fiatCurrencyCode={fiatCurrencyCode}
                loading={isLoading}
                loadingSpacing="my-5"
                onCtaClick={(modalType) => {
                  setActionType(modalType);
                  setSelectedWallet(null);
                  setShowDepositModal(true);
                }}
                isDepositHidden={isDepositHidden}
                wallets={filteredWallets}
              />
              {isLoading && <SettingButtonPlaceholder />}
              {!isLoading && canManageAccounts && (
                <ActionButton
                  icon="icon-wallet"
                  label={translate('manage_accounts_label')}
                  disabled={isWalletsCreationsDisabled}
                  onClick={() => {
                    setShowAddWallet(true);
                  }}
                  subLabel={translate('manage_your_accounts_sublabel')}
                />
              )}
            </LeftPanel>
            <Body>
              <AccountsContent
                filteredWallets={filteredWallets}
                ledgerAccounts={ledgerAccounts}
                isLoading={isLoading}
                searchInput={searchInput}
                showEmptyAccountScreen={showEmptyAccountScreen}
                userRegionSettings={userRegionSettings}
                selectedWalletType={selectedWalletType}
                onDepositClick={onDepositClick}
                onWithdrawalClick={onWithdrawalClick}
                onSendClick={onSendClick}
                onExchangeClick={onExchangeClick}
                onChangeSearchInput={onChangeSearchInput}
                setSelectedWalletType={setSelectedWalletType}
              />
            </Body>
          </>
        }
        mobileChildren={
          <>
            <div
              className="d-flex flex-row justify-content-between align-items-center px-3"
              style={{ justifyContent: 'space-between' }}>
              <h1 className="p-0 m-0">{translate('accounts_tab_label')}</h1>
              {canManageAccounts && (
                <ActionButton
                  disabled={isWalletsCreationsDisabled}
                  onClick={(e) => {
                    e.target.closest('html').classList.add('overflow-hidden');
                    setShowAddWallet(true);
                  }}
                />
              )}
            </div>
            <Balance
              isDepositHidden={isDepositHidden}
              totalChangeInFiat={totalChangeInFiatValue}
              totalBalanceInFiat={totalBalanceInFiatValue}
              loading={isLoading}
              showModal={showModal}
            />
            <div className="px-3">
              <WalletsFilter
                onChangeType={setSelectedWalletType}
                onChangeSearch={onChangeSearchInput}
                selectedTab={selectedWalletType}
              />
            </div>
            <WalletsList
              selectedWalletType={selectedWalletType}
              onDepositClick={onDepositClick}
              onWithdrawalClick={onWithdrawalClick}
              onSendClick={onSendClick}
              onExchangeClick={onExchangeClick}
              isWalletsLoading={isLoading}
              wallets={filteredWallets}
              ledgerAccounts={ledgerAccounts}
              refetchWallets={refetchAllWallets}
              searchValue={searchInput || ''}
              userRegionSettings={userRegionSettings || {}}
              showEmptyAccounts={showEmptyAccountScreen}
              showNoSearchResultFound={searchInput !== '' && filteredWallets?.length === 0}
            />
          </>
        }>
        {isUsUser && cardProvider === cardProviders.GALILEO ? renderDisclaimer() : <></>}
      </AccountLayout>
      {showDepositModal && (
        <WalletDepositModal
          hide={() => {
            setShowDepositModal(false);
            setSelectedWallet(null);
          }}
          walletsType={selectedWalletType}
          wallet={selectedWallet}
          isDeposit={isDeposit}
          actionType={actionType}
          openAddAccount={() => setShowAddWallet(true)}
          openSendCrypto={onSendClick}
          refreshWallets={refetchAllWallets}
        />
      )}
      {showAddAccountModal && (
        <AddAccountModal
          allowedCurrencies={enabledLedgerFilteredCurrencies}
          exitedWallets={exitedWallets}
          hide={() => setShowAddWallet(false)}
        />
      )}
      {showSendCryptoModal && (
        <SendCryptoModal
          hide={() => {
            setShowSendCryptoModal(false);
          }}
          wallet={selectedWallet}
          wallets={wallets}
          ledgerAccount={ledgerAccountWallet(selectedWallet)}
          fiatCurrencyCode={fiatCurrencyCode}
          onSent={refetchAllWallets}
          onChangeWallet={setSelectedWallet}
          initialScreen={SendCryptoScreensFlow.SEND_CRYPTO}
        />
      )}
      {showExchangeModal && (
        <ExchangeModal
          wallet={selectedWallet}
          hide={toggleExchangeModal}
          fiatCurrencyCode={fiatCurrencyCode}
          supportedCurrencies={supportedCurrencies}
          isUserAccountPage
        />
      )}
      <ModalLayout
        withButtons={false}
        onHide={() => setIsModalVisible(false)}
        isVisible={isModalVisible}
        currentScreen={currentModalScreen}>
        <InfoPopup keyName={MODAL_SCREENS.TOTAL_CHANGE_INFO} options={infoPopupOptions} />
      </ModalLayout>
    </>
  );
};

export default Accounts;
